import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/spinner';
import { verifyLaunch } from '@/api/cafe24Api';

/**
 * 카페24 App URL 런치 파라미터 진입점(§6). 설치 이후 매번 관리자가 앱
 * 아이콘을 클릭하면 mall_id+user_name+hmac 쿼리스트링과 함께 여기로 온다.
 *
 * 2026-08-25: B/E Cafe24LaunchVerificationService의 hmac 검증이 실제 구현으로
 * 교체됐고, 실제 카페24 관리자에서 발급된 hmac으로 서버 사이드 교차검증까지
 * 마쳤다. 등록된 App URL이 사이트 루트("/")라 이 라우트로 바로 오지 않고
 * LandingPage가 먼저 받아 여기로 리다이렉트해준다 - 그 감지 로직이 없어서
 * 이 화면 자체가 아예 호출되지 않던 버그도 함께 수정됨(LandingPage.tsx 참고).
 *
 * 2026-08-27 발견(카페24 앱스토어 심사 반려 "OAuth 인증실패" 조사): 카페24가
 * 최초 "설치" 클릭에도 hmac을 붙여 보내는 경우, 여기까지는 오지만 아직 OAuth
 * 동의 전이라 B/E에 이 몰의 Cafe24MallConnection이 없어 `verify()`가
 * "연동되지 않은 카페24 몰입니다"(IllegalArgumentException → HTTP 400,
 * errorType=VALIDATION_ERROR)로 실패한다. 기존엔 이 경우도 그냥 로그인 안내
 * 에러 화면만 보여줘 설치가 막다른 길이었다 - errorType으로 이 케이스를
 * 구분해 /cafe24/install-landing(§5.1 온보딩, 로그인 확인 → OAuth 인가)으로
 * 넘겨 설치가 계속 진행되도록 한다. hmac 위변조(errorType=INVALID_STATE)는
 * 보안상 그대로 에러 처리.
 */
export const Cafe24Launch: React.FC = () => {
  const navigate = useNavigate();
  const { applySession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const queryString = window.location.search.replace(/^\?/, '');
    if (!queryString) {
      setError('카페24 런치 파라미터가 없습니다.');
      return;
    }

    (async () => {
      try {
        const authData = await verifyLaunch(queryString);
        applySession(authData, true);
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const errorType = axios.isAxiosError(err) ? err.response?.data?.errorType : undefined;
        const mallId = new URLSearchParams(queryString).get('mall_id');
        if (errorType === 'VALIDATION_ERROR' && mallId) {
          navigate({ pathname: '/cafe24/install-landing', search: `?mall_id=${encodeURIComponent(mallId)}` }, { replace: true });
          return;
        }
        setError('카페24 관리자 인증에 실패했습니다. 로그인 후 이용해주세요.');
      }
    })();
  }, [navigate, applySession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center px-4">
        {error ? (
          <>
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={() => navigate('/login')} className="text-primary underline">
              로그인 페이지로 이동
            </button>
          </>
        ) : (
          <>
            <Spinner size="2xl" className="mx-auto mb-4 text-primary" />
            <p className="text-gray-600">카페24 인증 확인 중...</p>
          </>
        )}
      </div>
    </div>
  );
};
